/**
 * ResidualTARAV2 — entry component for the Residual TARA tab,
 * mounted at `/project/:projectId/residual-tara` (became the
 * canonical Residual TARA tab after the v1 sunset on 2026-05-27,
 * which retired `Step10ResidualTARA.jsx` and the legacy step page).
 *
 * Owns its own phase/step state via `residualReducer`. AppShell
 * chrome is a 2-zone grid (rail + content area) with
 * `ResidualPhaseRail`, an init-landing empty state when
 * `getResidualProgress` returns null (residual not initialised yet),
 * and one body per phase under `components/phases/`.
 */
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnalysisEmpty as AnalysisEmptyJsx } from '../../components/ui/Empty';
import { LoadingState } from '../../components/ui/LoadingState';
import { useWorkflowMachine } from '../athena/contexts/WorkflowMachineContext';
import { useWorkflowSession } from '../athena/contexts/WorkflowSessionContext';
import { residualReducer } from './state/residualReducer';
import {
  initialResidualState,
  PHASE_TO_RAIL_STOP,
  RAIL_STOPS,
  RailStop,
} from './state/types';
import { useResidualProgress } from './hooks/useResidualProgress';
import ResidualPhaseRail from './components/ResidualPhaseRail/ResidualPhaseRail';
import LoopBackBanner from './components/LoopBackBanner/LoopBackBanner';
import ControlsImplementationPhase from './components/phases/ControlsImplementationPhase/ControlsImplementationPhase';
import RequirementsReviewPhase from './components/phases/RequirementsReviewPhase/RequirementsReviewPhase';
import AttackPathResidualPhase from './components/phases/AttackPathResidualPhase/AttackPathResidualPhase';
import ResidualRiskPhase from './components/phases/ResidualRiskPhase/ResidualRiskPhase';
import CompletionPhase from './components/phases/CompletionPhase/CompletionPhase';
import styles from './ResidualTARAV2.module.css';

// LoadingState is a JSX primitive; cast for use from TSX (mirrors the
// ButtonAny convention).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
// AnalysisEmpty is a JSX component; cast for use from TSX.
const AnalysisEmpty = AnalysisEmptyJsx as unknown as React.ComponentType<any>;

export default function ResidualTARAV2() {
  const workflow = useWorkflowMachine();
  const workflowSession = useWorkflowSession();
  const sessionId: string | null = workflowSession?.backendSession?.sessionId ?? null;

  const [state, dispatch] = useReducer(residualReducer, initialResidualState);

  const actions = useResidualProgress({
    sessionId,
    backendService: workflow?.backendService,
    workflowStepNavigation: workflow?.stepNavigation,
    state,
    dispatch,
  });

  const handleSelectStop = useCallback(
    (stop: RailStop) => dispatch({ type: 'SET_SELECTED_STOP', payload: stop }),
    [],
  );

  /* ---------- URL ↔ reducer sync (?tab=…) ---------- */

  const [searchParams, setSearchParams] = useSearchParams();

  // One-shot seed: on first mount, jump to the rail stop encoded in the
  // URL (if any). After that, the state-→-URL effect below owns sync.
  // Guards against URL-injected garbage (`?tab=bogus`) — only canonical
  // RailStop values reach the reducer.
  const urlSeededRef = useRef(false);
  useEffect(() => {
    if (urlSeededRef.current) return;
    urlSeededRef.current = true;
    const tabParam = searchParams.get('tab');
    if (tabParam != null && RAIL_STOPS.includes(tabParam as RailStop)) {
      dispatch({ type: 'SET_SELECTED_STOP', payload: tabParam as RailStop });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-initialise once the initial residual fetch has confirmed
  // no session exists. Fires exactly once via the ref guard; if it
  // fails, the backend's ValueError surfaces in `state.error` and
  // InitLanding renders the error card.
  //
  // Trigger: initial residual fetch settled (loading=false), no
  // existing session (data=null), no prior error, sessionId known,
  // and the ref hasn't fired yet.
  const autoInitFiredRef = useRef(false);
  useEffect(() => {
    if (autoInitFiredRef.current) return;
    if (state.data !== null) return; // Already initialised
    if (state.loading) return; // Initial residual fetch still in flight
    if (state.error) return; // Don't retry after failure
    if (!sessionId) return;
    autoInitFiredRef.current = true;
    (async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        await actions.initialize();
      } catch (e: any) {
        dispatch({
          type: 'SET_ERROR',
          payload: e?.message ?? 'Failed to initialise Residual TARA',
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    })();
  }, [state.data, state.loading, state.error, sessionId, actions]);

  // Two main UI states:
  //   1. Empty / not-initialised — `data === null` after fetch
  //      → render init landing in the content area
  //   2. Initialised — data populated
  //      → render phase body for `selectedStop`
  const isInitialised = state.data !== null;
  const activeStop = isInitialised && state.backendPhase
    ? PHASE_TO_RAIL_STOP[state.backendPhase]
    : null;
  const stopToShow = state.selectedStop ?? activeStop;

  // Effective shown stop → URL `?tab=`. `replace: true` keeps every
  // rail click off the browser history stack (no per-stop clutter).
  useEffect(() => {
    if (!stopToShow) return;
    setSearchParams(
      (prev) => {
        if (prev.get('tab') === stopToShow) return prev;
        const next = new URLSearchParams(prev);
        next.set('tab', stopToShow);
        return next;
      },
      { replace: true },
    );
  }, [stopToShow, setSearchParams]);
  return (
    <div className={styles.shell}>
      <ResidualPhaseRail
        backendPhase={state.backendPhase}
        selectedStop={stopToShow}
        onSelect={handleSelectStop}
      />
      <main className={styles.content}>
        {/* Errors during phase operations (control update, transition,
            etc) render in a banner above the phase content. Errors
            during initialise are owned by InitLanding instead, so
            this banner only appears once a session is initialised. */}
        {state.error && isInitialised && (
          <div className={styles.errorBanner}>
            <strong>Error:</strong> {state.error}
          </div>
        )}

        {/* Loop-back banner — fires when a Reduce decision cycled
            the workflow back to controls_pending. Visible on every
            phase until the user dismisses it. */}
        {isInitialised && state.loopBackPending && (
          <LoopBackBanner
            onDismiss={() => dispatch({ type: 'CLEAR_LOOP_BACK' })}
          />
        )}

        {!isInitialised ? (
          <InitLanding loading={state.loading} error={state.error} />
        ) : stopToShow === 'controls' ? (
          <ControlsImplementationPhase state={state} actions={actions} />
        ) : stopToShow === 'requirements' ? (
          <RequirementsReviewPhase state={state} actions={actions} />
        ) : stopToShow === 'attack_paths' ? (
          <AttackPathResidualPhase state={state} actions={actions} />
        ) : stopToShow === 'risks' ? (
          <ResidualRiskPhase state={state} actions={actions} />
        ) : stopToShow === 'completion' ? (
          <CompletionPhase state={state} actions={actions} />
        ) : null}
      </main>
    </div>
  );
}

/* ============================================================
   Pre-initialised state. ResidualTARAV2 auto-fires
   `initializeResidualTARA(sessionId)` once the initial residual
   fetch confirms no session exists — there's no user-facing
   button. Two render shapes:
     • Error — auto-init failed. The backend's exact message
       (typically the two ValueErrors from
       `ResidualProgressService.extract_tara_data`: "Cybersecurity
       concept not found - complete initial TARA first" or "No
       risks with 'Reduce' treatment found…") renders in a
       warning-tinted card with a refresh hint.
     • Loading — auto-init in flight or initial fetch still
       running. One unified card. Success path: state.data flips
       non-null and this component unmounts in favour of the phase
       content.
   ============================================================ */
function InitLanding({
  loading,
  error,
}: {
  loading: boolean;
  error: string | null;
}) {
  if (error) {
    // The two known backend ValueErrors are expected "nothing to do
    // here yet" states rather than failures. Map each to a
    // customer-readable message on the shared AnalysisEmpty chassis so
    // Residual TARA reads like every other thread-dependent tab — the
    // raw backend text is never shown to the user.
    const lower = error.toLowerCase();
    // Analysis never started (no thread_id) — the backend returns
    // "Session <id> has no thread_id - TARA not started". Show the same
    // customer-readable no-analysis empty every other tab shows.
    const isNotStarted =
      lower.includes('no thread_id') || lower.includes('tara not started');
    const isNoConcept = lower.includes('cybersecurity concept');
    const isNoReduce =
      lower.includes('reduce') &&
      (lower.includes('risk') || lower.includes('treatment'));

    if (isNotStarted) {
      return (
        <div className={styles.landing}>
          <AnalysisEmpty />
        </div>
      );
    }
    if (isNoConcept) {
      return (
        <div className={styles.landing}>
          <AnalysisEmpty
            icon="shield-check"
            title="Analysis not complete yet"
            description="Finish the TARA workflow through the Cybersecurity Concept step so its controls and requirements are generated, then refresh to start residual analysis."
          />
        </div>
      );
    }
    if (isNoReduce) {
      return (
        <div className={styles.landing}>
          <AnalysisEmpty
            icon="shield-check"
            title="No residual analysis needed"
            description="No risks were marked for treatment (Reduce), so there's nothing left to re-assess for this project."
          />
        </div>
      );
    }
    return (
      <div className={styles.landing}>
        <AnalysisEmpty
          icon="error"
          title="Couldn't start residual analysis"
          description={error}
        />
      </div>
    );
  }

  return (
    <div className={styles.landing}>
      <LoadingStateAny
        variant="page"
        title="Setting up Residual TARA&hellip;"
        description={loading ? 'Initialising…' : 'Preparing…'}
      />
    </div>
  );
}
